import { Reveal } from "../landing/Reveal";
import type { UseCaseConfig } from "../../use-cases/types";

interface MomentsProps {
  moments: NonNullable<UseCaseConfig["moments"]>;
}

/**
 * Grid of lifecycle/milestone moments the vertical applies to. Pairs visually
 * with `Pillars` (same top-border tile treatment) but renders narrower tiles
 * to fit five-across on large screens, opening into a lifecycle row.
 *
 * Optional per-tile `eyebrow` gives text-only emphasis (e.g. "FLAGSHIP" on
 * the most prominent moment) without needing a badge or icon.
 */
export const Moments = ({ moments }: MomentsProps) => (
  <section className="bg-white py-24 md:py-32 px-6 md:px-16 lg:px-24">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-4">
          {moments.eyebrow}
        </span>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-display text-[clamp(2rem,3.6vw,3rem)] font-normal leading-tight text-fwd-ink mb-16 max-w-3xl">
          {moments.headline}
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
        {moments.items.map((m, i) => (
          <Reveal key={m.title} delay={Math.min(i * 0.08, 0.32)}>
            <div className="border-t-2 border-fwd-ink pt-5">
              {m.eyebrow && (
                <span className="text-fwd-red text-[0.6rem] font-bold tracking-[0.2em] uppercase block mb-2">
                  {m.eyebrow}
                </span>
              )}
              <h3 className="font-display text-lg md:text-xl text-fwd-ink leading-snug mb-2">
                {m.title}
              </h3>
              <p className="font-serif text-fwd-ink/70 text-base leading-relaxed">
                {m.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
